import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiSearch } from '../api/client.ts';
import { usePageSearch } from '../components/pageSearch.ts';
import { queryDefaults } from '../api/queries.ts';
import { useSession } from '../auth/session.tsx';
import { Chip, Pager, Waiting, listState, useUrlFilters } from '../components/DashboardFrame.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { formatDate } from '../dates.ts';

/**
 * `/visits` — Visits due: who needs seeing, soonest and most overdue first.
 *
 * GET /visits/due, which existed with no page. Row security decides whose: a
 * case manager sees their own caseload, the Director of Social Work everyone.
 * It opens on "overdue or due within 30 days", the number on the toolbar, so
 * the badge and the list agree; "All" shows every active client owed a visit.
 * Since 114 the list is one query over the caseload, not a function per client.
 */

interface DueRow {
  client_id: string;
  client_ref: string;
  display_name: string;
  currently_at: string | null;
  lives_at: string | null;
  last_visit: string | null;
  due_on: string | null;
  days_until: number | null;
  is_overdue: boolean;
  visits_this_year: number;
  required_this_year: number;
  basis: string;
  case_manager: string | null;
  unassigned: boolean;
}

interface DuePage {
  total: number;
  limit: number | null;
  offset: number;
  visits: DueRow[];
}

const PAGE = 50;

export function VisitsDue() {
  const { getToken } = useSession();
  const [params, update] = useUrlFilters();
  const show = params.get('show') ?? 'soon';
  const offset = Number(params.get('offset') ?? '0') || 0;
  // The typed search in history state, never the URL (pageSearch.ts).
  const [q, setSearch] = usePageSearch();
  const [typed, setTyped] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (q !== typed.trim()) setSearch(typed.trim(), ['offset']);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed]);

  const search = {
    limit: PAGE, offset, sort: '-priority', q,
    soon: show === 'soon' ? 'true' : undefined,
    overdue: show === 'overdue' ? 'true' : undefined,
    unassigned: show === 'unassigned' ? 'true' : undefined,
  };

  const list = useQuery({
    queryKey: ['visits', 'due', search],
    queryFn: () => apiSearch<DuePage>('/visits/due', search, getToken),
    placeholderData: keepPreviousData,
    ...queryDefaults,
  });
  const { failure, takeover } = listState(list);

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Visits due</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Who needs seeing, most urgent first. A visit is due a set interval after the last one, from the visiting
            standard that applies; overdue is past that and its grace.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {[
              ['soon', 'Overdue or due in 30 days'],
              ['overdue', 'Overdue'],
              ['unassigned', 'No case manager'],
              ['all', 'All'],
            ].map(([key, label]) => (
              <Chip key={key} active={show === key} onClick={() => update({ show: key === 'soon' ? null : key!, offset: null })}>
                {label}
              </Chip>
            ))}
            <label htmlFor="due-q" className="sr-only">Find a client in this list</label>
            <input id="due-q" type="search" value={typed} onChange={(e) => setTyped(e.target.value)}
                   placeholder="Name, reference, place or case manager"
                   className="min-h-[44px] w-full rounded-lg border border-neutral-300 px-3 text-base sm:ml-auto sm:w-80" />
          </div>
        </header>

        <div className="page-body">
          {failure && takeover ? (
            <LoadFailure what="Visits due" error={failure} onRetry={() => void list.refetch()}
                         retrying={list.isFetching} forbiddenIsSession={false} />
          ) : !list.data ? (
            <Waiting>Loading…</Waiting>
          ) : list.data.visits.length === 0 ? (
            <p className="px-4 py-8 text-center text-neutral-600">Nobody in this list.</p>
          ) : (
            <>
              <p className="px-4 pt-3 text-sm text-neutral-500 tabular-nums lg:px-5">{list.data.total.toLocaleString()} clients</p>
              <ul className="divide-y divide-neutral-100">
                {list.data.visits.map((v) => (
                  <li key={v.client_id}>
                    <Link to={`/clients/${v.client_id}`}
                          className="grid min-h-[56px] gap-x-4 gap-y-0.5 px-4 py-2.5 hover:bg-neutral-50 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_10rem_7rem] md:items-center lg:px-5">
                      <span className="min-w-0">
                        <span className="font-semibold text-neutral-900">{v.display_name}</span>{' '}
                        <span className="font-mono text-sm text-neutral-500">{v.client_ref}</span>
                        <span className="block truncate text-sm text-neutral-600">
                          {v.case_manager ?? <span className="font-semibold text-amber-800">No case manager</span>}
                        </span>
                      </span>
                      <span className="min-w-0 truncate text-sm text-neutral-700">{v.currently_at ?? v.lives_at ?? 'Not recorded'}</span>
                      <span className={`text-sm ${v.is_overdue ? 'font-semibold text-critical-edge' : 'text-neutral-800'}`}>
                        {v.due_on ? formatDate(v.due_on) : 'No anchor date'}
                        {v.days_until !== null && (
                          <span className="block text-xs font-normal text-neutral-600">
                            {v.is_overdue ? `overdue ${-v.days_until} days` : v.days_until < 0 ? 'within grace' : `in ${v.days_until} days`}
                          </span>
                        )}
                      </span>
                      <span className="text-sm text-neutral-700 tabular-nums">
                        {v.visits_this_year} of {v.required_this_year} this year
                        <span className="block text-xs text-neutral-500">{v.last_visit ? `last ${formatDate(v.last_visit)}` : 'never visited'}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Pager offset={offset} total={list.data.total} pageSize={PAGE} onOffset={(o) => update({ offset: o ? String(o) : null })} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
